import { useSelector } from 'react-redux';

import { PAYEMENT_TRACK_EVENT, INITIAL_META, SITE } from '~/components/payment-beta/utils/constants';
import { selectIsNewDepositFlow } from '~/components/brand/ducks/selectors';
import { insertTrackEvent } from '@comeon/analytics';

interface DataLayerEvent {
	event: string;
	pixel_country?: string;
	pixel_franchiseName?: string;
	pixel_playerId?: string;
	pixel_language?: string;
	pixel_lastDeposit?: boolean;
	pixel_last_payment_method?: string;
	pixel_screen?: string;
	pixel_deposit_action?: string;
	[key: string]: unknown;
}

interface AnalyticsTrackEvent {
	category?: string;
	action?: string;
	label?: string;
	value?: string;
}

interface UpdatedData extends Omit<DataLayerEvent, 'event'> {
	event?: string;
}

export interface LocationState {
	paymentPixelData?: DataLayerEvent;
}

const filterList = ['pixel_field_validation'];
const filterSpecificKeys = (obj: Record<string, unknown>) =>
	Object.fromEntries(Object.entries(obj).filter(([key]) => !filterList.includes(key)));

const usePaymentTracking = () => {
	const isNewDepositFlowEnabled = useSelector(selectIsNewDepositFlow);

	const getEventData = (): DataLayerEvent => {
		const newDataLayer: DataLayerEvent = window.dataLayer.findLast(
			(e: DataLayerEvent) => e.event === PAYEMENT_TRACK_EVENT
		) ||
			window.dataLayer.findLast((e: DataLayerEvent) => e.event === INITIAL_META && e.frame === SITE) || {
				event: PAYEMENT_TRACK_EVENT
			}; // Default 'event' property to avoid the error

		// Create bonus-related data if the selectedBonus has a description
		const bonusEventData: Record<string, unknown> = {
			pixel_info_banner: 'none',
			pixel_page_elements: 'none'
		};

		if (newDataLayer.event === PAYEMENT_TRACK_EVENT) {
			return filterSpecificKeys({ ...newDataLayer, ...bonusEventData }) as DataLayerEvent;
		}

		return {
			event: PAYEMENT_TRACK_EVENT,
			pixel_country: newDataLayer.pixel_country || 'none',
			pixel_franchiseName: newDataLayer.pixel_franchiseName || 'none',
			pixel_playerId: newDataLayer.pixel_playerId || 'none',
			pixel_language: newDataLayer.pixel_language || 'none',
			pixel_deposit_type: (newDataLayer.pixel_deposit_type ?? newDataLayer.pixel_lastDeposit) ? 'non-ftd' : 'ftd',
			pixel_last_payment_method: newDataLayer.pixel_last_payment_method || 'none',
			...bonusEventData
		};
	};

	const trackEvent = (updatedData: UpdatedData): void => {
		if (!isNewDepositFlowEnabled) return;
		const finalEventData: DataLayerEvent = {
			...getEventData(),
			...updatedData
		};

		if (window?.dataLayer) {
			window.dataLayer.push(finalEventData);
		} else {
			// eslint-disable-next-line no-console
			console.error('Data layer is not available.');
		}
	};

	const trackAnalyticsEvent = (customEvent: AnalyticsTrackEvent): void => {
		if (!isNewDepositFlowEnabled) return;

		const eventData = getEventData();
		const depositType = eventData.pixel_deposit_type || 'ftd';

		const defaultEvent = {
			category: 'Deposit',
			action: depositType === 'non-ftd' ? 'Recurring - Failed' : 'First - Failed',
			label: '',
			value: '0'
		};

		const finalEvent = {
			...defaultEvent,
			...customEvent
		};

		insertTrackEvent(finalEvent);
	};

	return { trackEvent, trackAnalyticsEvent };
};

export default usePaymentTracking;
